import { jsPDF } from 'jspdf';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { getSettings, getEntries, formatShortDate } from './storage';

const isNative = () => Capacitor.isNativePlatform();
const getPlatform = () => Capacitor.getPlatform();

// ============================================================
// CSV EXPORT — uses answer1..answer10 as column headers
// ============================================================
export const generateCSV = (entries, startDate, endDate) => {
  const settings = getSettings();
  const currentGoals = (settings?.goals || []).sort((a, b) => a.order - b.order);
  const prompts = (settings?.prompts || []).sort((a, b) => a.order - b.order);

  const filteredEntries = Object.entries(entries)
    .filter(([date]) => date >= startDate && date <= endDate)
    .sort(([a], [b]) => b.localeCompare(a));

  if (filteredEntries.length === 0) return 'No entries found for the selected period.';

  // Collect all unique goals from all entries (using snapshots or current)
  const allGoalsMap = new Map();
  filteredEntries.forEach(([date, entry]) => {
    const goalsForEntry = entry.goalSnapshots || currentGoals;
    goalsForEntry.forEach(goal => {
      if (!allGoalsMap.has(goal.id)) {
        allGoalsMap.set(goal.id, { id: goal.id, text: goal.text });
      }
    });
  });
  const allGoals = Array.from(allGoalsMap.values());

  // Headers
  const headers = ['date'];
  allGoals.forEach(goal => {
    headers.push(`Goal: ${goal.text}`);
  });
  prompts.forEach(p => headers.push(p.answerKey));
  headers.push('ai_response');

  const rows = filteredEntries.map(([date, entry]) => {
    const row = [date];

    // Use entry's own snapshots (or fallback to current goals)
    const goalsForEntry = entry.goalSnapshots || currentGoals;

    allGoals.forEach(goal => {
      const entryGoal = goalsForEntry.find(g => g.id === goal.id);
      const status = entry.goals?.[goal.id];
      let statusText = '–';
      if (entryGoal) {
        const goalCap = entryGoal.text.charAt(0).toUpperCase() + entryGoal.text.slice(1);
        const easyCap = entryGoal.easyText ? entryGoal.easyText.charAt(0).toUpperCase() + entryGoal.easyText.slice(1) : '';
        if (status?.completed && status?.easyActivity) statusText = goalCap;
        else if (status?.completed) statusText = goalCap;
        else if (status?.easyActivity) statusText = easyCap || goalCap;
      }
      row.push(statusText);
    });

    prompts.forEach(p => {
      row.push(entry[p.answerKey] || '–');
    });

    row.push(entry.aiResponse || '–');
    return row;
  });

  return [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
};


// ============================================================
// PDF EXPORT
// ============================================================
export const generatePDF = (entries, startDate, endDate) => {
  const settings = getSettings();
  const currentGoals = (settings?.goals || []).sort((a, b) => a.order - b.order);
  const prompts = (settings?.prompts || []).sort((a, b) => a.order - b.order);
  const doc = new jsPDF();

  const filteredEntries = Object.entries(entries)
    .filter(([date]) => date >= startDate && date <= endDate)
    .sort(([a], [b]) => b.localeCompare(a));

  // Title
  doc.setFontSize(20);
  doc.setTextColor(45, 45, 53);
  doc.text('RE-BUILD-ME Daily Log Export', 20, 20);
  doc.setFontSize(10);
  doc.setTextColor(115, 115, 125);
  doc.text(`Exported: ${new Date().toLocaleDateString()}`, 20, 28);
  doc.text(`Period: ${formatShortDate(startDate)} - ${formatShortDate(endDate)}`, 20, 34);

  let yPos = 50;
  const pageH = 280;
  const mL = 20;

  filteredEntries.forEach(([date, entry], idx) => {
    if (yPos > pageH - 60) { doc.addPage(); yPos = 20; }

    doc.setFontSize(14);
    doc.setTextColor(107, 91, 149);
    doc.text(formatShortDate(date), mL, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setTextColor(45, 45, 53);

    // Use entry's own snapshots (or fallback to current goals)
    const goalsForEntry = entry.goalSnapshots || currentGoals;

    goalsForEntry.forEach(goal => {
      const status = entry.goals?.[goal.id];
      if (status?.completed || status?.easyActivity) {
        const goalCap = goal.text.charAt(0).toUpperCase() + goal.text.slice(1);
        const easyCap = goal.easyText ? goal.easyText.charAt(0).toUpperCase() + goal.easyText.slice(1) : '';
        let displayText = goalCap;
        if (status?.easyActivity && !status?.completed && easyCap) displayText = easyCap;
        doc.text(`Goal achieved: ${displayText}`, mL, yPos);
        yPos += 6;
      }
    });
    yPos += 4;

    prompts.forEach(prompt => {
      const val = entry[prompt.answerKey];
      if (val && val !== '–') {
        doc.setFontSize(9);
        doc.setTextColor(115, 115, 125);
        doc.text(`${prompt.question}:`, mL, yPos);
        yPos += 5;
        doc.setTextColor(45, 45, 53);
        const lines = doc.splitTextToSize(val, 170);
        lines.forEach(line => {
          if (yPos > pageH - 20) { doc.addPage(); yPos = 20; }
          doc.text(line, mL, yPos);
          yPos += 5;
        });
        yPos += 2;
      }
    });

    if (entry.aiResponse) {
      if (yPos > pageH - 30) { doc.addPage(); yPos = 20; }
      doc.setFontSize(9);
      doc.setTextColor(142, 110, 204);
      doc.text('AI Companion:', mL, yPos);
      yPos += 5;
      doc.setFontSize(10);
      doc.setTextColor(45, 45, 53);
      const aiLines = doc.splitTextToSize(entry.aiResponse, 170);
      aiLines.forEach(line => {
        if (yPos > pageH - 20) { doc.addPage(); yPos = 20; }
        doc.text(line, mL, yPos);
        yPos += 5;
      });
      yPos += 2;
    }

    yPos += 10;
    if (idx < filteredEntries.length - 1) {
      doc.setDrawColor(233, 227, 245);
      doc.line(mL, yPos - 5, 190, yPos - 5);
    }
  });

  return doc;
};

// ============================================================
// NATIVE FILE SHARING
// ============================================================
const shareFileNative = async (content, filename) => {
  try {
    const writeResult = await Filesystem.writeFile({
      path: filename,
      data: content,
      directory: Directory.Cache,
    });

    const platform = getPlatform();
    if (platform === 'ios') {
      await Share.share({ files: [writeResult.uri], title: filename, dialogTitle: `Share ${filename}` });
    } else {
      await Share.share({ title: filename, url: writeResult.uri, dialogTitle: `Share ${filename}` });
    }
    return true;
  } catch (error) {
    console.error('Error sharing file:', error);
    throw error;
  }
};

const downloadFileWeb = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ============================================================
// PUBLIC EXPORT FUNCTIONS
// ============================================================
export const exportCSV = async (startDate, endDate) => {
  const entries = getEntries();
  const csv = generateCSV(entries, startDate, endDate);
  const filename = `rebuildme_${startDate}_to_${endDate}.csv`;

  if (isNative()) {
    const base64 = btoa(unescape(encodeURIComponent(csv)));
    await shareFileNative(base64, filename);
    return { success: true, native: true };
  }
  downloadFileWeb(csv, filename, 'text/csv');
  return { success: true, native: false };
};

export const exportPDF = async (startDate, endDate) => {
  const entries = getEntries();
  const doc = generatePDF(entries, startDate, endDate);
  const filename = `rebuildme_${startDate}_to_${endDate}.pdf`;

  if (isNative()) {
    const pdfBase64 = doc.output('datauristring').split(',')[1];
    await shareFileNative(pdfBase64, filename);
    return { success: true, native: true };
  }
  doc.save(filename);
  return { success: true, native: false };
};

export const exportBackup = async (data) => {
  const json = JSON.stringify(data, null, 2);
  const filename = `rebuildme_backup_${new Date().toISOString().split('T')[0]}.json`;

  if (isNative()) {
    const base64 = btoa(unescape(encodeURIComponent(json)));
    await shareFileNative(base64, filename);
    return { success: true, native: true };
  }
  downloadFileWeb(json, filename, 'application/json');
  return { success: true, native: false };
};
